// NPC dialog (qa-runs#1167): the "Dialogue" tool. Every line this person heard, newest at the bottom, with the speaker
// and the time — a player's own lines only, *(to you)* on one aimed at them; the DM's every line with who heard it and
// Delete. The DM (and a co-DM who may change tokens) says a line here: by a token on this scene or by nobody (a name
// such as *Narrator*, or none), to everyone on the scene or to chosen players. It is not chat: players only read.
import { useEffect, useRef, useState } from 'react';
import { DIALOG_LIMITS, memberLabel, preparedOf, type Cue, type DmObject } from '@dragons-table/vtt-shared';
import { listCues } from '../../sound';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useShell } from '../context';
import { dialogs, heardWords, useDialog, type Line } from '../../dialog';
import { BTN } from '../ui';

const btn = `${BTN} shrink-0`;
const input = 'w-full rounded border border-stone-700 bg-stone-900 px-2 py-1 text-sm';
const timeOf = (at: string) => { const d = new Date(at); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); };

/**
 * A token's prepared lines (qa-runs#1168): the DM's prep on the token at this table, never sent to a player. Each is a
 * button that says it at once, to the scene or the players chosen above; up to 20 a token.
 */
function PreparedLines({ speaker, say }: { speaker: string; say: (text: string) => void }) {
  const { act, setNotice } = useTableState();
  const { campaignId } = useShell();
  const [cues, setCues] = useState<Cue[]>([]);
  const [adding, setAdding] = useState('');
  const reload = async () => { const got = await listCues(campaignId); if (got) setCues(got); };
  useEffect(() => { void reload(); }, [campaignId, speaker]);
  const held = preparedOf(cues, speaker);
  const lines = held?.say?.lines ?? [];
  const save = (next: string[]) => {
    const done = (r: { ok: boolean; reason?: string }) => { if (r.ok) void reload(); else setNotice(r.reason ?? 'Those lines could not be kept.'); };
    if (!next.length) { if (held) act({ kind: 'cue.remove', id: held.id }, done); return; }
    act({ kind: 'cue.set', cue: { id: held?.id ?? crypto.randomUUID(), trigger: { on: 'prepared', objectId: speaker }, channel: 'effect', say: { lines: next, speaker } } }, done);
  };
  const add = () => { const t = adding.trim(); if (!t || lines.length >= DIALOG_LIMITS.prepared) return; save([...lines, t]); setAdding(''); };
  return (
    <div className="space-y-1 text-xs" data-testid="dialog-prepared">
      <p className="text-stone-400">Prepared lines (yours alone until said)</p>
      {lines.map((l, i) => (
        <div key={i} className="flex items-center gap-1">
          <button type="button" className={`${btn} flex-1 truncate text-left`} data-testid="dialog-prepared-line" title={l} onClick={() => say(l)}>{l}</button>
          <button type="button" className={btn} aria-label={`Remove the prepared line "${l}"`} onClick={() => save(lines.filter((_, k) => k !== i))}>×</button>
        </div>
      ))}
      {lines.length < DIALOG_LIMITS.prepared && (
        <div className="flex gap-1">
          <input className={input} data-testid="dialog-prepared-new" maxLength={DIALOG_LIMITS.text} value={adding} placeholder="That'll be 15 gold, no haggling." onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <button type="button" className={btn} data-testid="dialog-prepared-add" disabled={!adding.trim()} onClick={add}>Keep</button>
        </div>
      )}
    </div>
  );
}

function SayForm() {
  const { store, setNotice } = useTableState();
  const { table } = useShell();
  const mayAct = useMayAct();
  const { speaker: picked } = useDialog();
  const players = table.members.filter((m) => m.role === 'player');
  const tokens = Object.values(store.state.objects as Record<string, DmObject>).filter((o) => o.kind === 'token').sort((a, b) => a.name.localeCompare(b.name));
  const [speaker, setSpeaker] = useState<string>('');
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [aim, setAim] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  // The token menu's Say…: that token speaks, and the line is typed at once.
  useEffect(() => { if (picked) { setSpeaker(picked); dialogs.pickSpeaker(null); setTimeout(() => box.current?.focus(), 0); } }, [picked]);
  const send = async (words = text) => {
    if (!words.trim() || busy || (aim && !chosen.length)) return;
    setBusy(true);
    const r = await dialogs.say({ text: words, ...(speaker ? { speaker } : {}), ...(name.trim() ? { name: name.trim() } : {}), ...(aim ? { to: chosen } : {}) });
    setBusy(false);
    if (r.ok) { if (words === text) setText(''); } else setNotice(r.reason ?? 'That line could not be said.');
  };
  return (
    <form className="space-y-1 rounded border border-stone-700 p-2" aria-label="Say a line" data-testid="dialog-say" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      <label className="block text-xs">Speaker
        <select className={input} data-testid="dialog-speaker" value={speaker} onChange={(e) => setSpeaker(e.target.value)}>
          <option value="">No token (a voice, the narrator)</option>
          {tokens.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}{o.hidden ? ' (hidden)' : ''}</option>)}
        </select>
      </label>
      {speaker && mayAct('cue.set') && <PreparedLines speaker={speaker} say={(l) => { void send(l); }} />}
      <label className="block text-xs">Name shown (optional)
        <input className={input} data-testid="dialog-name" maxLength={DIALOG_LIMITS.name} value={name} placeholder={speaker ? "The token's name as the players know it" : 'Narrator'} onChange={(e) => setName(e.target.value)} />
      </label>
      <textarea ref={box} className={`${input} min-h-16`} aria-label="Line" data-testid="dialog-text" maxLength={DIALOG_LIMITS.text} value={text}
        placeholder="You're not from around here." onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
      <fieldset className="text-xs">
        <label className="mr-2"><input type="radio" checked={!aim} onChange={() => setAim(false)} /> Everyone on the scene</label>
        <label><input type="radio" data-testid="dialog-aim" checked={aim} onChange={() => setAim(true)} /> Chosen players</label>
        {aim && (
          <div className="mt-1 flex flex-wrap gap-2" data-testid="dialog-to">
            {players.map((p) => (
              <label key={p.id} className="flex items-center gap-1">
                <input type="checkbox" checked={chosen.includes(p.id)} onChange={(e) => setChosen((c) => (e.target.checked ? [...c, p.id] : c.filter((x) => x !== p.id)))} />
                {memberLabel(p, store.state.objects as never, {})}
              </label>
            ))}
            {!players.length && <span className="text-stone-400">No players in this campaign.</span>}
          </div>
        )}
      </fieldset>
      <div className="flex items-center gap-2">
        <button type="submit" className={btn} data-testid="dialog-send" disabled={busy || !text.trim() || (aim && !chosen.length)}>Say</button>
        <span className="text-xs text-stone-400">{text.length}/{DIALOG_LIMITS.text}</span>
      </div>
    </form>
  );
}

function LineRow({ line, dm }: { line: Line; dm: boolean }) {
  const { store, setNotice } = useTableState();
  const { table } = useShell();
  const nameOf = (id: string) => memberLabel(table.members.find((m) => m.id === id), store.state.objects as never, {});
  const heard = dm ? heardWords(line, nameOf) : null;
  return (
    <li className="rounded border border-stone-800 p-1" data-testid="dialog-line">
      <div className="flex items-baseline gap-2 text-xs text-stone-400">
        <span className="font-semibold text-stone-200" data-testid="dialog-line-name">{line.name || '—'}</span>
        {line.toYou && <span data-testid="dialog-to-you">(to you)</span>}
        <span className="ml-auto">{timeOf(line.at)}</span>
      </div>
      <p className="whitespace-pre-wrap break-words" data-testid="dialog-line-text">{line.text}</p>
      {dm && (
        <div className="flex items-center gap-2 text-xs text-stone-400">
          <span className="flex-1">{heard}{'cue' in line && line.cue ? ' · said by a cue' : ''}</span>
          <button type="button" className={btn} data-testid="dialog-delete" onClick={() => { void dialogs.remove(line.id).then((r) => { if (!r.ok) setNotice(r.reason ?? 'That line could not be deleted.'); }); }}>Delete</button>
        </div>
      )}
    </li>
  );
}

export function DialoguePanel() {
  const { role, viewingAs } = useTableState();
  const mayAct = useMayAct();
  const { lines, loaded } = useDialog();
  const speaks = !viewingAs && (role === 'dm' || role === 'codm') && mayAct('dialog.say');
  const dm = speaks;
  const end = useRef<HTMLDivElement>(null);
  // Newest at the bottom, kept in view as lines arrive.
  useEffect(() => { end.current?.scrollIntoView?.({ block: 'end' }); }, [lines.length]);
  return (
    <div className="flex h-full flex-col gap-2 p-2 text-sm" data-testid="dialog-panel">
      <ol className="flex-1 space-y-1 overflow-y-auto" aria-label="NPC dialog" aria-live="polite" data-testid="dialog-lines">
        {lines.map((l) => <LineRow key={l.id} line={l} dm={dm} />)}
        {loaded && !lines.length && <li className="text-xs text-stone-400" data-testid="dialog-none">{speaks ? 'No lines yet. Pick a speaker and say one below.' : 'No lines yet. What NPCs say to you appears here.'}</li>}
        <div ref={end} />
      </ol>
      {speaks && <SayForm />}
    </div>
  );
}
